import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import styles from './Button.module.css';
import Icon from './Icon';
import type { IconName } from './icons';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: ReactNode;
  /** Defaults to `secondary`. Reach for `primary` only for the one action a view
      is actually about, and `danger` only for something irreversible. */
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  iconEnd?: IconName;
  /** Shows in-button progress and stops the press going through. The label
      stays put, because a button whose text is replaced by a spinner loses the
      only thing telling the user what they are waiting for. */
  loading?: boolean;
  fullWidth?: boolean;
}

const ICON_SIZE: Record<ButtonSize, number> = { sm: 13, md: 15, lg: 17 };

/**
 * Wrapped in `forwardRef` so callers can reach the underlying element. This is
 * not optional polish: on React 18 a plain function component silently drops a
 * `ref`, which is how `Menu`'s Escape-returns-focus-to-the-trigger stopped
 * working the first time a Button was used as a trigger.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children,
    variant = 'secondary',
    size = 'md',
    icon,
    iconEnd,
    loading = false,
    fullWidth = false,
    className,
    disabled,
    onClick,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = [
    styles.button,
    styles[variant],
    styles[size],
    fullWidth ? styles.fullWidth : '',
    loading ? styles.loading : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={disabled}
      aria-busy={loading || undefined}
      onClick={loading ? undefined : onClick}
      {...rest}
    >
      {loading ? (
        <span className={styles.tide} aria-hidden="true">
          <span />
          <span />
          <span />
        </span>
      ) : icon ? (
        <Icon name={icon} size={ICON_SIZE[size]} />
      ) : null}
      {children}
      {iconEnd && !loading ? <Icon name={iconEnd} size={ICON_SIZE[size]} /> : null}
    </button>
  );
});

export default Button;
